import { useApiClient } from "@/hooks/useApiClient";
import useSWR from "swr";
import { useEffect } from "react";
import { toast } from "react-toastify";
import {
  type CompanyListAPIResponse,
  type UseCompanyListResult,
} from "../hooks/useCompanyList";

export interface CompanyListBulkAPIRequest {
  corporateNumbers: string[];
  closed: boolean;
  kubun: string;
}

// 商業登記受付帳から遷移する際に複数の会社法人等番号を一括で検索するフック
export const useCompanyListBulk = (
  fetchParams: CompanyListBulkAPIRequest
): UseCompanyListResult => {
  const { apiClient } = useApiClient();
  const fetcher = async (
    url: string,
    params: CompanyListBulkAPIRequest
  ): Promise<CompanyListAPIResponse> => {
    const { data } = await apiClient.get<CompanyListAPIResponse>(url, {
      params: {
        corporateNumbers: params.corporateNumbers,
        closed: params.closed,
        kubun: params.kubun,
      },
      // ?corporateNumbers=A&corporateNumbers=B 形式（[] 無し）で送るため
      paramsSerializer: { indexes: null },
    });
    return data;
  };

  const { data, isLoading } = useSWR<CompanyListAPIResponse>(
    fetchParams.corporateNumbers.length > 0
      ? ["/commercial-picture-request/search-bulk/", fetchParams]
      : null,
    async ([url, params]: [string, CompanyListBulkAPIRequest]) =>
      await fetcher(url, params),
    { revalidateOnFocus: false }
  );

  // toast はレンダー本体ではなく data 確定時の effect でのみ発火させる（親の再レンダーで重複発火しないように）
  useEffect(() => {
    if (data === undefined) return;
    if (data.body.length === 0 && (data.failures ?? []).length === 0) {
      toast.info("検索結果が0件です。");
    }
  }, [data]);

  // データがまだ取得されていない場合に動作
  if (data === undefined) return { data: [], isLoading };

  // 失敗した会社法人等番号のリストと失敗原因を取得
  const failures = data.failures ?? [];

  // 検索結果が0件で、かつ失敗もない場合
  if (data.body.length === 0 && failures.length === 0) {
    return { data: [], isLoading: false };
  }

  // 失敗明細がある場合。成功分（body）の有無で一部失敗／全件失敗を判定する
  const bulkSearchIssue =
    failures.length > 0
      ? {
          isPartialSuccess: data.body.length > 0,
          detail: data.detail ?? "",
          failures,
        }
      : undefined;

  return { data: data.body, isLoading: false, bulkSearchIssue };
};
